/**
 * Validate the ServiceNow credential bundle and bounded feed selection.
 *
 * This pure boundary keeps submission orchestration focused on persistence and
 * makes the four-part ServiceNow credential rule explicit without coupling it
 * to rendered fields or transport errors.
 */
import type { ConnectorCredentialSource } from '../../types'
import type {
  ServiceNowConnectionStatus,
  ServiceNowFilter,
  ServiceNowWorkspaceCredentials,
} from '../types'

export const SERVICENOW_DEFAULT_API_URL = 'https://dev-itsupport.umd.edu'

export function isServiceNowApiUrl(value: string): boolean {
  try {
    const url = new URL(value)
    const approvedHost = url.hostname === 'dev-itsupport.umd.edu'
      || url.hostname === 'itsupport.umd.edu'
      || /.+\.service-now\.com$/.test(url.hostname)
    return value === value.trim()
      && url.protocol === 'https:'
      && approvedHost
      && !url.username
      && !url.password
      && !url.port
      && (url.pathname === '/' || url.pathname === '')
      && !url.search
      && !url.hash
  } catch {
    return false
  }
}

export function serviceNowFormError({
  apiUrl,
  recordLimit,
  filters,
  credentialSource,
  connection,
  credentials,
}: {
  apiUrl: string
  recordLimit: number
  filters: ServiceNowFilter[]
  credentialSource: ConnectorCredentialSource
  connection: ServiceNowConnectionStatus
  credentials: ServiceNowWorkspaceCredentials
}): string | undefined {
  if (!isServiceNowApiUrl(apiUrl)) {
    return 'Enter an approved ServiceNow HTTPS instance URL'
  }
  if (!Number.isInteger(recordLimit) || recordLimit < 1 || recordLimit > 500) {
    return 'Enter a ServiceNow record limit from 1 to 500'
  }
  if (filters.length > 8 || filters.some(filter => !filter.value.trim())) {
    return 'Complete each ServiceNow filter or remove it'
  }
  const values = Object.values(credentials)
  const completeBundle = values.every(Boolean)
  if (credentialSource === 'workspace' && values.some(Boolean) && !completeBundle) {
    return 'Complete all four ServiceNow credential fields'
  }
  if (
    credentialSource === 'workspace'
    && !connection.workspaceConfigured
    && !completeBundle
  ) {
    return 'Enter the ServiceNow system credentials'
  }
  if (credentialSource === 'umd' && connection.umdConfigured !== true) {
    return 'Configure the ServiceNow keys in UMD Credential Store first'
  }
  return undefined
}

export function hasCompleteServiceNowCredentials(
  credentials: ServiceNowWorkspaceCredentials,
) {
  return Object.values(credentials).every(Boolean)
}
